/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Pill, Plus, X, Sparkles, ShieldAlert, ShieldCheck, Loader2, History, Info, ArrowRight,
} from 'lucide-react';
import { checkDrugInteractions, listMyDrugChecks } from '@/lib/ai_clinical';
import { cn } from '@/lib/utils';

const riskMeta = {
  low:      { label: 'Basso',        color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
  moderate: { label: 'Moderato',     color: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  high:     { label: 'Alto',         color: 'bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/30' },
  severe:   { label: 'Grave',        color: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30' },
  unknown:  { label: 'Da verificare', color: 'bg-muted text-muted-foreground border-border' },
};

const severityMeta = {
  minor:           { label: 'Minore',           color: 'bg-muted text-muted-foreground' },
  moderate:        { label: 'Moderata',         color: 'bg-amber-500/10 text-amber-700 dark:text-amber-300' },
  major:           { label: 'Grave',            color: 'bg-orange-500/10 text-orange-700 dark:text-orange-300' },
  contraindicated: { label: 'Controindicata',   color: 'bg-red-500/10 text-red-700 dark:text-red-300' },
};

const DrugChip = ({ name, onRemove }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-xs font-medium px-2.5 py-1" data-testid={`drug-chip-${name}`}>
    {name}
    <button type="button" onClick={onRemove} className="hover:bg-primary/20 rounded-full p-0.5" aria-label={`Rimuovi ${name}`}><X className="h-3 w-3" /></button>
  </span>
);

const DrugCheckerPage = () => {
  const [drugs, setDrugs] = useState([]);
  const [drugInput, setDrugInput] = useState('');
  const [conditions, setConditions] = useState('');
  const [allergies, setAllergies] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    try {
      const { items } = await listMyDrugChecks();
      setHistory(items || []);
    } catch (e) {
      console.error('DrugChecker: load history failed', e);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const addDrug = () => {
    const v = drugInput.trim();
    if (!v) return;
    if (drugs.includes(v)) { toast.info('Farmaco già presente'); return; }
    setDrugs([...drugs, v]);
    setDrugInput('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (drugs.length === 0) { toast.error('Aggiungi almeno un farmaco'); return; }
    setLoading(true);
    setResult(null);
    try {
      const res = await checkDrugInteractions({
        medications: drugs,
        patient_conditions: conditions ? conditions.split(',').map((s) => s.trim()).filter(Boolean) : undefined,
        allergies: allergies ? allergies.split(',').map((s) => s.trim()).filter(Boolean) : undefined,
        notes: notes || undefined,
      });
      setResult(res.result);
      loadHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore nell’analisi');
    } finally {
      setLoading(false);
    }
  };

  const riskInfo = riskMeta[result?.overall_risk] || riskMeta.unknown;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 lg:py-10" data-testid="drug-checker-page">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-primary"><Sparkles className="h-5 w-5" /><span className="text-xs uppercase tracking-wider font-bold">Nexi AI Farmacologia</span></div>
        <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mt-1">Controllo Interazioni Farmacologiche</h1>
        <p className="text-muted-foreground text-sm md:text-base mt-1 max-w-2xl">Analizza rapidamente interazioni, controindicazioni e alternative più sicure per una terapia.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-5">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><Pill className="h-5 w-5 text-primary" />Farmaci & contesto</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Farmaci</Label>
                  <div className="flex gap-2">
                    <Input value={drugInput} onChange={(e) => setDrugInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDrug(); } }} placeholder="Es. Warfarin 5mg" data-testid="drug-input" />
                    <Button type="button" variant="secondary" onClick={addDrug} data-testid="drug-add-btn"><Plus className="h-4 w-4" /></Button>
                  </div>
                  {drugs.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2" data-testid="drug-list">
                      {drugs.map((d) => (
                        <DrugChip key={d} name={d} onRemove={() => setDrugs(drugs.filter((x) => x !== d))} />
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="conds">Patologie del paziente (opzionale)</Label>
                  <Input id="conds" value={conditions} onChange={(e) => setConditions(e.target.value)} placeholder="Ipertensione, insufficienza renale, ..." data-testid="drug-conditions-input" />
                </div>
                <div>
                  <Label htmlFor="allergies">Allergie note (opzionale)</Label>
                  <Input id="allergies" value={allergies} onChange={(e) => setAllergies(e.target.value)} placeholder="Penicillina, FANS, ..." data-testid="drug-allergies-input" />
                </div>
                <div>
                  <Label htmlFor="notes">Note aggiuntive (opzionale)</Label>
                  <Textarea id="notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="drug-notes-input" placeholder="Es. paziente anziano, funzione renale ridotta…" />
                </div>
                <Button type="submit" disabled={loading || drugs.length === 0} className="w-full" data-testid="drug-analyze-btn">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Analisi in corso…</> : <><Sparkles className="h-4 w-4" /> Analizza interazioni</>}
                </Button>
              </form>
            </CardContent>
          </Card>

          {result && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="drug-result">
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className={cn('gap-1', riskInfo.color)} data-testid="drug-overall-risk">
                      {(result.overall_risk === 'low' ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />)}
                      Rischio complessivo: {riskInfo.label}
                    </Badge>
                  </div>
                </CardHeader>
                {result.summary && (
                  <CardContent>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{result.summary}</p>
                  </CardContent>
                )}
              </Card>

              {result.interactions?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Interazioni rilevate ({result.interactions.length})</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {result.interactions.map((it) => {
                      const sev = severityMeta[it.severity] || severityMeta.moderate;
                      return (
                        <div key={`${it.drug_a}-${it.drug_b}-${it.severity}`} className="rounded-xl border border-border/60 p-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold">{it.drug_a}</span>
                            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="font-semibold">{it.drug_b}</span>
                            <Badge variant="outline" className={cn('ml-auto', sev.color)}>{sev.label}</Badge>
                          </div>
                          {it.mechanism && <p className="text-xs text-muted-foreground mt-2"><span className="font-semibold">Meccanismo:</span> {it.mechanism}</p>}
                          {it.clinical_effect && <p className="text-xs mt-1"><span className="font-semibold">Effetto clinico:</span> {it.clinical_effect}</p>}
                          {it.management && <p className="text-xs mt-1 text-primary"><span className="font-semibold">Gestione:</span> {it.management}</p>}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              )}

              {result.contraindications?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Controindicazioni</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {result.contraindications.map((c) => (
                      <div key={`${c.drug}-${c.reason?.slice(0,20)}`} className="text-sm">
                        <span className="font-semibold">{c.drug}</span> — <span className="text-muted-foreground">{c.reason}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {result.allergy_warnings?.length > 0 && (
                <Alert>
                  <ShieldAlert className="h-4 w-4" />
                  <AlertDescription>
                    <ul className="list-disc pl-4 space-y-1 text-xs">
                      {result.allergy_warnings.map((a) => <li key={a}>{a}</li>)}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {result.safer_alternatives?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Alternative più sicure</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {result.safer_alternatives.map((a) => (
                      <div key={`${a.replace}-${a.with}`} className="text-sm flex items-center gap-2 flex-wrap">
                        <Badge variant="outline">{a.replace}</Badge>
                        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                        <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">{a.with}</Badge>
                        {a.note && <span className="text-xs text-muted-foreground">— {a.note}</span>}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {result.monitoring_required?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Monitoraggio consigliato</CardTitle></CardHeader>
                  <CardContent>
                    <ul className="list-disc pl-4 text-sm space-y-1">
                      {result.monitoring_required.map((m) => <li key={m}>{m}</li>)}
                    </ul>
                  </CardContent>
                </Card>
              )}

              {result.disclaimer && (
                <p className="text-[11px] text-muted-foreground text-center"><Info className="h-3 w-3 inline mr-1" />{result.disclaimer}</p>
              )}
            </motion.div>
          )}
        </div>

        <aside className="space-y-3">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><History className="h-4 w-4" />Analisi recenti</CardTitle></CardHeader>
            <CardContent className="space-y-2 max-h-[60vh] overflow-y-auto scrollbar-thin">
              {history.length === 0 ? (
                <div className="text-xs text-muted-foreground">Nessuna analisi precedente.</div>
              ) : history.map((h) => (
                <button
                  key={h.id}
                  onClick={() => { setDrugs(h.input?.medications || []); setResult(h.result); }}
                  className="w-full text-left rounded-xl border border-border/60 p-2.5 hover:bg-accent/40 transition-colors"
                  data-testid={`drug-history-${h.id}`}
                >
                  <div className="text-xs font-medium truncate">{(h.input?.medications || []).slice(0, 4).join(', ')}{(h.input?.medications || []).length > 4 && '…'}</div>
                  <div className="text-[10px] text-muted-foreground mt-1">{new Date(h.created_at).toLocaleString('it')}</div>
                </button>
              ))}
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
};

export default DrugCheckerPage;
